import { Empty, IconBadge } from "@zfp-akademie-a-s/zfp-design";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { QuestionnaireProvider } from "Components/Analyses/Questionnaire/QuestionnaireContext";
import { useGetAnalysisQuestionnaire } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { Questionnaire } from "Infrastructure/ApiZfp/Schema";
import { ANALYZER_EMAIL } from "Infrastructure/Config/constants";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useContext } from "react";

type Props = {
  children: (data: Questionnaire) => ReactNode;
};

/**
 * Loads the questionnaire for every PDF template page and puts it into print mode, so the pages themselves
 * only decide what part of the questionnaire they render.
 */
export const PdfQuestionnaireBoundary = ({ children }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data, isPending, isError } = useGetAnalysisQuestionnaire(analysisId, versionId);
  const isErrorNSurePlans = (data?.nsurePlans?.length ?? 0) === 0 && (data?.goals?.length ?? 0) > 0;

  const errorContent = (
    <Empty
      title={t(Resources.Questionnaire.Error.Title)}
      text={t(Resources.Questionnaire.Error.Text, { email: ANALYZER_EMAIL })}
      iconSize={40}
      icon={<ErrorIcon sx={{ color: "grey.400" }} />}
    />
  );

  if (isPending) {
    return null;
  }

  if (isError || !data || isErrorNSurePlans) {
    return errorContent;
  }

  if (data.goals.length === 0) {
    return (
      <Empty
        title={t(Resources.Questionnaire.NoGoals.Title)}
        text={t(Resources.Questionnaire.NoGoals.Text)}
        icon={<IconBadge color="grey" icon={<ErrorIcon sx={{ fontSize: 35 }} />} />}
      />
    );
  }

  const questionnaire = data.questionnaire;
  if (!questionnaire) {
    return errorContent;
  }

  return (
    <QuestionnaireProvider
      goalsData={data.goals ?? []}
      allQuestions={(questionnaire.questionnaireQuestions ?? []).filter((q) => q.subType !== "PayoutOnly")}
      groups={questionnaire.questionnaireGroups ?? []}
      investmentPlans={data.nsurePlans ?? []}
      organizedBy=""
      isReadOnly
      mode="print"
    >
      {children(data)}
    </QuestionnaireProvider>
  );
};
